// P24 (démo de Besançon du 09/10, D4) — la vue « Structure » : un anneau pour la RÉPARTITION
// d'un seul territoire.
//
// Olivier a demandé un camembert ; Marc : « demande réalisable », mais « un camembert par
// territoire comparé serait lourd ». D'où les deux règles de ce module, et la raison de son
// existence à part (testé seul, comme `echelles.ts`) :
//
//  1. On ne propose l'anneau QUE sur une vraie partition : toutes les parts connues (aucun
//     N/D, aucune valeur négative), et leur somme égale à 100 % — ou au total servi — à
//     l'arrondi près. Un anneau dessiné sur des parts qui ne font pas le tout ferait lire
//     comme « le reste » un morceau qui n'existe pas : c'est une donnée inventée par la
//     forme, la doctrine du dépôt l'interdit.
//  2. Un SEUL territoire par anneau. Marc, 13/10 (23 h) : quand le graphique en montre
//     plusieurs (échelles d'un empilé, commune ajoutée par « Comparer à »), le bouton ne se
//     désactive plus — l'anneau montre la commune, et chaque autre territoire tracé est une
//     « strate » qu'on choisit d'un clic. Un territoire dont les parts ne font pas le tout
//     (règle 1) n'a pas de strate : on ne dessine pas son anneau.
//
// Rien n'est recalculé : l'anneau reçoit les valeurs SERVIES (déjà des parts quand l'unité
// est « % »), réordonnées seulement — la catégorie d'un empilé est une série, elle devient
// une part.
import type { ChartSpec } from '../../observatoire/ChartBlock'
import { libelleCategorie } from './format'

/** Au-delà, la palette des anneaux (8 tons, `themeGraphiques`) recyclerait ses couleurs :
 *  deux parts de la même couleur ne se distinguent plus. Les barres restent alors seules. */
export const PARTS_MAX = 8

/** Ce que l'anneau dessine : une part par catégorie, d'un seul territoire. */
export interface Structure {
  categories: string[]
  parts: number[]
  abs?: (number | null)[]
  absLabel?: string
  /** Le nom du territoire (barre d'un empilé, ou libellé de la série). */
  territoire: string
}

/** Un territoire tracé dont l'anneau peut se dessiner. `nom` : ce qu'on écrit sur sa puce et au
 *  centre de l'anneau — absent quand la série unique d'un groupé porte le nom de sa MESURE
 *  (« % des ménages ») et non celui d'un territoire. */
export interface Strate {
  cle: string
  nom?: string
  structure: Structure
}

export type EtatStructure =
  | { etat: 'absent' }                         // pas une répartition : aucun bouton
  | { etat: 'ok'; strates: Strate[] }          // la première : la commune (ou le 1er tracé)

/** Décimales d'un nombre servi (0,1 → 1) — pour juger l'arrondi des parts. */
function decimales(v: number): number {
  const s = String(v)
  const i = s.indexOf('.')
  return i < 0 ? 0 : Math.min(s.length - i - 1, 6)
}

/** Les parts forment-elles le tout ? `cible` = 100 (unité « % ») ou le total servi.
 *  Tolérance : la demi-unité du dernier chiffre servi, par part, plafonnée à 1 point —
 *  six parts arrondies à l'entier peuvent sommer à 99 ou 101, jamais à 97. */
export function sommeAuTout(parts: readonly number[], cible: number): boolean {
  const pas = 0.5 * 10 ** -Math.max(0, ...parts.map(decimales))
  const echelle = cible / 100
  const tolerance = Math.max(pas, Math.min(parts.length * pas, echelle))
  const somme = parts.reduce((s, v) => s + v, 0)
  return Math.abs(somme - cible) <= tolerance + 1e-9
}

/** Partition d'UN territoire : catégories, parts, absolus — ou `null`. */
export function partitionUnTerritoire(spec: ChartSpec): Structure | null {
  if (spec.type !== 'bar' || spec.histo_comparable || spec.clic_annees) return null
  // La cible : 100 si l'unité est un pourcentage (pas « ‰ » : un taux pour mille n'est pas
  // une part du tout), sinon le total que le serveur DÉCLARE. Sans l'un ni l'autre, rien
  // ne dit que des décomptes forment un tout (un établissement peut relever de deux
  // familles, un risque en recouvre un autre) : pas d'anneau.
  const cible = spec.unit && /^\s*%/.test(spec.unit) ? 100
    : typeof spec.total === 'number' && spec.total > 0 ? spec.total : null
  if (cible === null) return null

  let s: Structure
  if (spec.empile) {
    // Empilé : une barre par territoire, une série par catégorie.
    if (spec.labels.length !== 1) return null
    const abs = spec.datasets.map(d => d.abs?.[0] ?? null)
    s = { categories: spec.datasets.map(d => libelleCategorie(d.label)),
          parts: spec.datasets.map(d => d.data[0] as number),
          ...(abs.some(a => a !== null) ? { abs, absLabel: spec.datasets[0]?.absLabel } : {}),
          territoire: libelleCategorie(spec.labels[0]) }
  } else {
    // Série unique : une barre par catégorie.
    if (spec.datasets.length !== 1) return null
    const d = spec.datasets[0]
    s = { categories: spec.labels.map(libelleCategorie), parts: d.data as number[],
          ...(d.abs ? { abs: d.abs, absLabel: d.absLabel } : {}), territoire: d.label }
  }
  const n = s.parts.length
  if (n < 2 || n > PARTS_MAX) return null
  if (!s.parts.every(v => typeof v === 'number' && Number.isFinite(v) && v >= 0)) return null
  if (!s.parts.some(v => v > 0)) return null
  return sommeAuTout(s.parts, cible) ? s : null
}

/** La barre `j` d'un empilé, seule (avec ses absolus). */
function barre(spec: ChartSpec, j: number): ChartSpec {
  return { ...spec, labels: spec.labels.slice(j, j + 1),
           datasets: spec.datasets.map(d => ({ ...d, data: d.data.slice(j, j + 1),
                                               ...(d.abs ? { abs: d.abs.slice(j, j + 1) } : {}) })) }
}

/** L'état du bouton « Structure ».
 *
 *  `spec` = le graphique servi, `effectif` = celui qui est tracé (échelles masquées ou
 *  communes ajoutées par « Comparer à »). Le graphique est une répartition si sa PREMIÈRE
 *  barre (la commune) en est une. Ses strates sont les territoires TRACÉS qui en sont une
 *  aussi, dans l'ordre du tracé : les barres d'un empilé (« Ma commune », EPCI, département,
 *  France, communes ajoutées), ou la série servie puis les communes ajoutées d'un groupé.
 *  Un groupé servi avec plusieurs séries reste sans anneau : ses séries ne sont pas forcément
 *  des territoires (hommes / femmes). */
/** Le libellé que le serveur donne à la commune sur les graphiques « commune et échelles ». */
export const LIBELLE_MA_COMMUNE = 'Ma commune'

/** `nomCommune` (revue de #773, Marc : « Besançon », pas « Ma commune ») : le nom que la page
 *  connaît ; il nomme la barre « Ma commune » d'un empilé et la série servie d'un groupé. */
export function etatStructure(spec: ChartSpec, effectif: ChartSpec,
                              nomCommune?: string | null): EtatStructure {
  const premiere = spec.empile ? barre(spec, 0) : spec
  if (!partitionUnTerritoire(premiere)) return { etat: 'absent' }
  const strates: Strate[] = []
  if (spec.empile) {
    effectif.labels.forEach((_, j) => {
      const s = partitionUnTerritoire(barre(effectif, j))
      if (s) strates.push({ cle: s.territoire, structure: s,
        nom: s.territoire === LIBELLE_MA_COMMUNE && nomCommune ? nomCommune : s.territoire })
    })
  } else {
    // La commune y est la série servie : son nom, ou « Ma commune » comme la puce fixe de
    // « Comparer à ». Sans nom ni comparaison, la série ne porte que le nom de sa MESURE.
    const compare = effectif.datasets.length > 1
    effectif.datasets.forEach((d, i) => {
      const s = partitionUnTerritoire({ ...effectif, datasets: [d] })
      const nom = i > 0 ? d.label : nomCommune || (compare ? LIBELLE_MA_COMMUNE : undefined)
      if (s) strates.push({ cle: d.label, structure: s, ...(nom ? { nom } : {}) })
    })
  }
  // Tout masqué, ou aucun territoire tracé qui fasse le tout : rien à dessiner en anneau.
  return strates.length ? { etat: 'ok', strates } : { etat: 'absent' }
}

/** Le nom d'un territoire au centre de l'anneau : des lignes coupées entre les mots, chacune
 *  tenant dans `largeur` ; au-delà de `maxLignes`, la dernière finit par une ellipse. `mesurer`
 *  est la mesure du texte dans la police du tracé (`ctx.measureText`). */
export function lignesCentre(texte: string, largeur: number, mesurer: (t: string) => number,
                             maxLignes = 3): string[] {
  const tient = (t: string) => mesurer(t) <= largeur
  // Raccourcit `t` jusqu'à ce que `t…` tienne.
  const ellipse = (t: string) => {
    let s = t
    while (s && !tient(`${s}…`)) s = s.slice(0, -1)
    return `${s.trimEnd()}…`
  }
  const lignes: string[] = []
  let ligne = ''
  for (const mot of texte.split(/\s+/).filter(Boolean)) {
    const essai = ligne ? `${ligne} ${mot}` : mot
    if (tient(essai)) { ligne = essai; continue }
    if (ligne) lignes.push(ligne)
    ligne = mot
  }
  if (ligne) lignes.push(ligne)
  const coupees = lignes.slice(0, maxLignes)
  const reste = lignes.length > maxLignes
  return coupees.map((l, i) =>
    (i === coupees.length - 1 && reste) || !tient(l) ? ellipse(l) : l)
}

/** Le spec de l'anneau : mêmes unité et note, catégories en libellés. */
export function specStructure(spec: ChartSpec, s: Structure): ChartSpec {
  return { type: 'doughnut', unit: spec.unit, total: spec.total, labels: s.categories,
           datasets: [{ label: s.territoire, data: s.parts,
                        ...(s.abs ? { abs: s.abs, absLabel: s.absLabel } : {}) }] }
}

/** Le texte d'une entrée de légende : la catégorie, sa valeur et sa part.
 *  Unité « % » : la valeur EST la part servie — on ne la redivise pas (revue codex P2 du
 *  12/09, un second pourcentage serait fabriqué). Total déclaré : la part se calcule sur ce
 *  total, celui du producteur. */
export function texteLegende(categorie: string, valeur: number, spec: ChartSpec,
                             fmt: (v: number) => string,
                             part: (v: number, total: number) => string): string {
  if (spec.unit && /^\s*%/.test(spec.unit)) return `${categorie} — ${fmt(valeur)} %`
  const u = spec.unit ? ` ${spec.unit}` : ''
  return spec.total ? `${categorie} — ${fmt(valeur)}${u} (${part(valeur, spec.total)} %)`
    : `${categorie} — ${fmt(valeur)}${u}`
}
